/* Exact description-to-supplies presentation adapter. No requests or data writes. */
(() => {
  if (customElements.get('miniami-pattern-supplies')) return;
  const clean = value => value.replace(/\s+/g, ' ').trim();
  const label = value => clean(value).replace(/^[^\p{L}\p{N}]+/u, '').replace(/:$/, '').toLowerCase();
  const forbidden = 'script,style,iframe,object,embed,form,button,input,video,audio,img';
  function lines(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const fragment = document.createDocumentFragment(); fragment.append(node.cloneNode()); return [fragment];
    }
    const brs = [...node.querySelectorAll('br')], result = [];
    const range = document.createRange(); range.selectNodeContents(node);
    for (const br of brs) {
      range.setEndBefore(br); result.push(range.cloneContents()); range.setStartAfter(br); range.setEnd(node, node.childNodes.length);
    }
    result.push(range.cloneContents()); return result;
  }
  function trimBullet(fragment) {
    const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
    let text;
    while ((text = walker.nextNode())) {
      if (!text.textContent.trim()) continue;
      text.textContent = text.textContent.replace(/^\s*[•*–-]\s+/, ''); break;
    }
    return fragment;
  }
  function extract(root) {
    const items = []; let active = '', beadParent = null, inventoryParent = null, colourGroup = null;
    const add = (fragment, structured = false) => {
      const text = clean(fragment.textContent);
      if (!text || fragment.querySelector(forbidden)) return;
      // An explicit palette subheading belongs to the preceding bead inventory,
      // not to the procurement checklist. Keep its exact words and colour lines.
      if (active === 'materials' && inventoryParent && /^(?:base|additional) colou?rs(?:\s*\([^)]*\))?:$/i.test(text)) {
        const heading = document.createElement('div'); heading.dataset.supplyGroupHeading = '';
        heading.append(fragment); colourGroup = document.createElement('ul');
        inventoryParent.append(heading, colourGroup); return;
      }
      const substantive = [...fragment.childNodes].filter(n => n.nodeType === Node.ELEMENT_NODE || clean(n.textContent));
      if (structured && beadParent && substantive.length === 1 && substantive[0].nodeType === Node.ELEMENT_NODE && /^(UL|OL)$/.test(substantive[0].tagName)) {
        beadParent.append(substantive[0]); return;
      }
      const li = document.createElement('li'); li.dataset.supplyKind = active; li.append(trimBullet(fragment));
      if (!structured && colourGroup && !/^\s*[•*–-]\s+/.test(text)) { colourGroup.append(li); return; }
      colourGroup = null;
      if (!structured && /^\s*-\s+/.test(text) && beadParent) {
        let nested = beadParent.querySelector(':scope > ul');
        if (!nested) { nested = document.createElement('ul'); beadParent.append(nested); }
        nested.append(li); return;
      }
      items.push(li);
      beadParent = /beads?\b.*\bin\s*$/i.test(text) ? li : null;
      inventoryParent = active === 'materials' && /(?:beads?\b|rocailles\b)/i.test(text) ? li : null;
    };
    const visit = node => {
      if (node.nodeType === Node.ELEMENT_NODE && /^(DIV|SECTION|ARTICLE)$/.test(node.tagName) && [...node.children].some(child => /^(DIV|SECTION|ARTICLE|P|UL|OL|H[1-6])$/.test(child.tagName))) {
        [...node.childNodes].forEach(visit); return;
      }
      if (node.nodeType === Node.ELEMENT_NODE && /^(UL|OL)$/.test(node.tagName)) {
        if (active) for (const original of node.children) {
          if (original.tagName !== 'LI') continue;
          const fragment = document.createDocumentFragment();
          [...original.childNodes].forEach(child => fragment.append(child.cloneNode(true))); add(fragment, true);
        }
        return;
      }
      for (const fragment of lines(node)) {
        const text = clean(fragment.textContent), heading = label(text);
        const headingMatch = heading.match(/^(tools|materials) needed$/);
        if (headingMatch) { active = headingMatch[1]; beadParent = inventoryParent = colourGroup = null; continue; }
        if (!text) continue;
        if (/^[-–—⸻_\s]+$/u.test(text) || /^(what (you|you’ll|you'll)|techniques used|finished size|please note|disclaimer|important notes|additional notes|exact colou?r references)/.test(heading) || /^✅/.test(text)) { active = false; beadParent = inventoryParent = colourGroup = null; continue; }
        if (node.nodeType === Node.ELEMENT_NODE && /^H[1-6]$/.test(node.tagName)) { active = false; beadParent = inventoryParent = colourGroup = null; continue; }
        if (active) add(fragment);
      }
    };
    [...root.childNodes].forEach(visit); return items;
  }
  // Compare only the same category's ordered merchant words. Whitespace and
  // list bullets are presentation, not a new bill of materials or fuzzy match.
  function quoteText(items) {
    const parts = []; let text = '';
    const flush = () => {
      const part = clean(text).replace(/^[•*–-]\s+/, '');
      if (part) parts.push(part); text = '';
    };
    const visit = node => {
      if (node.nodeType === Node.TEXT_NODE) { text += node.textContent; return; }
      const boundary = /^(UL|OL|LI|BR)$/.test(node.tagName) || node.hasAttribute?.('data-supply-group-heading');
      if (boundary) flush();
      [...node.childNodes].forEach(visit);
      if (boundary) flush();
    };
    items.forEach(item => { visit(item); flush(); }); return parts.join(' ');
  }
  class MiniamiPatternSupplies extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.isConnected || this.dataset.suppliesReady === 'true') return;
        const template = this.querySelector('template[data-native-pattern-description]');
        const list = this.querySelector('[data-pattern-supplies-list]');
        if (!template || !list) return;
        const items = extract(template.content), merged = [];
        for (const kind of ['tools', 'materials']) {
          const explicit = [...list.children].filter(li => li.dataset.supplyKind === kind);
          const native = items.filter(li => li.dataset.supplyKind === kind);
          // Equivalent typed quotes keep native nesting and links. A changed
          // category replaces only itself; a missing one still uses native.
          merged.push(...(explicit.length && (!native.length || quoteText(explicit) !== quoteText(native)) ? explicit : native));
        }
        // October17 C59/C60/C79: one colour presentation, only in the dot rail.
        // Keep the literal bead size/type and every non-colour requirement.
        const paletteNames = new Set([...this.querySelectorAll('[data-pattern-colour-label]')].map(n => label(n.textContent)));
        for (const [index, item] of merged.entries()) {
          if (item.dataset.supplyKind !== 'materials') continue;
          let movedColours = false;
          item.querySelectorAll('ul,ol').forEach(list => {
            if (paletteNames.size && list.children.length && [...list.children].every(n => paletteNames.has(label(n.textContent)))) {
              const heading = list.previousElementSibling;
              if (heading?.matches('[data-supply-group-heading]')) heading.remove();
              list.remove(); movedColours = true;
            }
          });
          // Native flat colour rows are moved by visibleItems below as well.
          const next = merged[index + 1];
          movedColours ||= next?.dataset.supplyKind === 'materials' && paletteNames.has(label(next.textContent));
          const text = item.textContent, terminal = text.match(/\s+in\s*:?\s*$/i);
          if (movedColours && /\bbeads?\b|\brocailles\b/i.test(text) && terminal) {
            // Remove only the orphaned introducer, even across rich text nodes.
            // Keep the merchant's anchors, nesting, emphasis and internal 'in'.
            const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT), nodes = [];
            let node;
            while ((node = walker.nextNode())) nodes.push(node);
            let remaining = terminal[0].length;
            for (const node of nodes.reverse()) {
              const count = Math.min(remaining, node.textContent.length);
              node.textContent = node.textContent.slice(0, node.textContent.length - count);
              remaining -= count;
              if (!remaining) break;
            }
          }
        }
        const visibleItems = merged.filter(item => item.dataset.supplyKind !== 'materials' || !paletteNames.has(label(item.textContent)));
        if (visibleItems.length) {
          list.replaceChildren(...visibleItems); this.querySelector('[data-pattern-supplies-block]').hidden = false; this.hidden = false;
        }
        this.dataset.suppliesReady = 'true'; this.dataset.suppliesCount = String(visibleItems.length);
      });
    }
    disconnectedCallback() { if (this.frame != null) cancelAnimationFrame(this.frame); this.frame = null; }
  }
  customElements.define('miniami-pattern-supplies', MiniamiPatternSupplies);
})();
